import useInViewport from '../util/useInViewport';

export class Timer {
  constructor({
    items = [],
    targets = [],
    targetActiveClass = 'active',
    triggerActiveClass = 'active',
    duration = 5000,
    timerCallback = () => {},
    onClickCallBack = () => {},
  }) {
    this.items = items;
    this.currentItemIndex = 0;
    this.triggerActiveClass = triggerActiveClass;
    this.targetActiveClass = targetActiveClass;
    this.targets = targets;
    this.timer = null;
    this.timerDuration = duration;
    this.running = false;
    this.remainingTime = this.timerDuration;
    this.loopTimeStarted = null;
    this.timerCallback = timerCallback;
    this.onClickCallBack = onClickCallBack;

    this.strings = {
      errorItemNotDefined: 'Items must be an array of elements',
    };

    this.init();
  }

  init() {
    if (!this.items || this.items.length === 0) {
      this.error(this.strings.errorItemNotDefined);

      return;
    }

    this.items.forEach((item) => {
      item.addEventListener('click', () => this.onItemClick(item));
    });
  }

  progressAnimationHandler(duration, index) {
    let startTime = null;
    let initialValue = 0;
    const progressBeforePause =
      this.items[this.currentItemIndex].dataset.startFrom;

    const step = (timestamp) => {
      if (!startTime) {
        startTime = timestamp;
      }

      const elapsed = timestamp - startTime;
      initialValue = Math.min(elapsed / duration, 1);

      if (progressBeforePause) {
        initialValue = Math.min(parseFloat(progressBeforePause) + elapsed / duration, 1);
      }

      this.items[index].style.setProperty('--progress', initialValue);
      this.items[index].dataset.progress = initialValue;

      this.items.forEach((item) => {
        if (item !== this.items[index]) {
          item.style.setProperty('--progress', 0);
          item.dataset.progress = 0;
          item.dataset.startFrom = 0;
        }

      });

      if (elapsed < duration && this.running) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }

  resume() {
    this.start(this.remainingTime);
  }

  reset() {
    if (!this.items || this.items.length === 0) {
      this.error(this.strings.errorItemNotDefined);

      return;
    }

    this.stop();

    this.items.forEach((item) => {
      this.resetItem(item);
    });

    this.currentItemIndex = 0;

    this.showTabOnButtonIndex(this.items[this.currentItemIndex]);

    this.start();
  }

  resetItem(item) {
    item.classList.remove(this.triggerActiveClass);
  }

  setItemActive(item) {
    item.classList.add(this.triggerActiveClass);
  }

  onProgress(time = this.timerDuration) {
    if (!this.items || this.items.length === 0) {
      this.error(this.strings.errorItemNotDefined);

      return;
    }

    this.loopTimeStarted = Date.now();

    this.setItemActive(this.items[this.currentItemIndex]);

    this.items.forEach((item) => {
      if (item !== this.items[this.currentItemIndex]) {
        this.resetItem(item);
      }
    });

    this.showTabOnButtonIndex(this.items[this.currentItemIndex]);
    this.progressAnimationHandler(time, this.currentItemIndex);

    clearTimeout(this.timer);

    this.timerCallback({
      index: this.currentItemIndex,
    });

    this.timer = setTimeout(() => {
      this.currentItemIndex += 1;

      if (this.currentItemIndex >= this.items.length) {
        this.currentItemIndex = 0;
      }

      this.remainingTime = this.timerDuration;
      this.onProgress();
    }, time);
  }

  pause() {
    this.stop();

    this.remainingTime = 0;

    this.remainingTime =
      this.timerDuration -
      ((this.loopTimeStarted - this.remainingTime) % this.timerDuration);

    this.items[this.currentItemIndex].dataset.startFrom =
      this.items[this.currentItemIndex].dataset.progress;
  }

  start(time) {
    if (this.running) return;

    this.running = true;

    this.onProgress(time || this.remainingTime);
  }

  stop() {
    if (!this.running) return;

    this.running = false;

    clearTimeout(this.timer);
  }

  showTabOnButtonIndex(item) {
    const { buttonIndex } = item.dataset;

    const target = this.getTargetByIndex(buttonIndex);

    if (!target) return;

    this.targets.forEach((targetElement) => {
      if (targetElement !== target) {
        targetElement.classList.remove(this.targetActiveClass);
      }

    } );

    target.classList.add(this.targetActiveClass);
  }

  getTargetByIndex(index) {
    if (!this.targets || this.targets.length === 0) {
      this.error('Targets must be an array of elements');

      return null;
    }

    return this.targets[index];
  }

  onItemClick(item) {
    this.stop();

    this.currentItemIndex = Number.parseInt(item.dataset.buttonIndex, 10);

    this.showTabOnButtonIndex(item);

    this.setItemActive(item);

    this.onClickCallBack({ index: this.currentItemIndex });

    this.items.forEach((item) => {
      if (item !== this.items[this.currentItemIndex]) {
        this.resetItem(item);
      }
    });

    setTimeout(() => {
      item.classList.add('b-af-what-we-do__nav-link--clicked')
      this.setItemProgressToFilled();
    }, 50);
  }

  setItemProgressToFilled() {
    this.items.forEach((item) => {
      this.changeProgressToFilled(item);
    });
  }

  error(message) {
    console.error(message);
  }

  changeProgressToFilled(item) {
    item.style.setProperty('--progress', 1);
  }
}

export default class WhatWeDo {
  constructor() {
    this.classNames = {
      block: 'b-af-what-we-do',
      button: 'b-af-what-we-do__nav-link',
      item: 'b-af-what-we-do__tab',
      media: 'b-af-what-we-do__column-media',
      lottie: 'c-new-lottie-animation',
      buttonLink: 'b-af-what-we-do__column-titl',
    };

    this.lottieSegments = {}
  }

  init() {
    const blocks = Array.from(document.querySelectorAll(`.${this.classNames.block}`))

    if (blocks.length === 0) return;
    blocks.forEach((block) => {
      const lottie = this.getLottieInBlock(block);

      if (lottie) {
        const { id } = lottie.dataset;

        window.addEventListener('lottie-ready', (event) => {
          const { id: loadedLottieId } = event.detail;

          if (loadedLottieId === id) {
            this.initBlock({ block });
          }
        })
      }

      const allButtons = Array.from(block.querySelectorAll('.b-af-what-we-do__column-title'));

      allButtons.forEach(button => {
          if (button.getAttribute('href') === '#') {
              button.addEventListener('click', function(event) {
                  event.preventDefault();
              });
              button.classList.add('disabled');
          }
      });
    });
  }

  initBlock({ block }) {
    this.resetLottieSegment({ block });

    const buttons = Array.from(block.querySelectorAll(`.${this.classNames.button}`));

    this.fillSegments({ buttons });

    const timer = new Timer({
      duration: parseInt(block.dataset.timer, 10) || 5000,
      items: buttons,
      triggerActiveClass: 'b-af-what-we-do__nav-link--active',
      targets: Array.from(block.querySelectorAll(`.${this.classNames.item}`)),
      targetActiveClass: 'b-af-what-we-do__tab--active',
      onClickCallBack: ({ index }) => {
        this.getLottieAndPlaySegment({ block, index });
      },
      timerCallback: ({ index }) => {
        this.getLottieAndPlaySegment({ block, index });
      },
    });

    const nav = block.querySelector('.b-af-what-we-do__nav');

    useInViewport(nav, () => {
      timer.start();
    }, ()=> {
      timer.stop();
    });
  }

  getLottieAndPlaySegment({ block, index }) {
    const lottie = this.getLottieInBlock(block);

    if (lottie) {
      const lottiePlayer = this.getLottieById(lottie.dataset.id);

      if (lottiePlayer) {
        lottiePlayer.goToAndPlay(this.getSegmentNameById(index), false);
      }
    }
  }

  fillSegments({ buttons }) {
    if (buttons.length === 0) {
      return;
    }

    buttons.forEach((button) => {
      const { segmentName, buttonIndex } = button.dataset;

      this.lottieSegments[buttonIndex] = segmentName;
    });
  }

  getSegmentNameById(id) {
    return this.lottieSegments[id];
  }

  resetLottieSegment({ block }) {
    const blockLottie = this.getLottieInBlock(block);

    if (blockLottie) {
      const lottiePlayer = this.getLottieById(blockLottie.dataset.id);

      if (lottiePlayer) {
        lottiePlayer.goToAndPlay(this.lottieSegments[0], false);
      }
    }
  }

  getLottieInBlock(block) {
    return block.querySelector(`.${this.classNames.lottie}`);
  }

  getLottieById(id) {
    return window.lottieAnimations[id];
  }
}




// WEBPACK FOOTER //
// ./scripts/blocks/whatWeDo.js